import { EMPTY, filter, map, take } from 'rxjs';

import type {
  ItinerarySearchCriteria,
  TripSearchCriteria,
  ItineraryResponse,
  SearchService,
  RecipeResponse,
  ItinerarySuggestions,
} from '@libs/trip-planner-interfaces';
import { GraphQL } from '@libs/trip-planner-interfaces';
import {
  isDefined,
  logged,
  performanceMark,
  PerformancePoint,
} from '@libs/utils';

import { getApolloClient } from './infrastructure';
import {
  SEARCH_ITINERARIES_SUBSCRIPTION,
  SEARCH_ITINERARIES_HOTEL_SELECTION_SUBSCRIPTION,
  SEARCH_ITINERARIES_PEEK_PRICE_SUBSCRIPTION,
  SEARCH_ITINERARY_SUGGESTION_PRICES_CHEAPEST_SUBSCRIPTION,
  SEARCH_ITINERARY_SUGGESTION_PRICES_MOST_POPULAR_SUBSCRIPTION,
} from './queries';
import { getRecipeQL } from './utils/requestMappers/recipeQL';
import { toItineraryResponse } from './utils/responseMappers/itineraryResponseQL';
import { toItinerarySuggestionPrices } from './utils/responseMappers/itinerarySuggestionPricesResponseQL';

let isFirstSearch = true;

// Test-only: lets specs reset the module-level isFirstSearch flag.
export function setIsFirstSearchForTesting(value: boolean): void {
  isFirstSearch = value;
}

// NOTE: priceAccuracyToken must only ever be attached to the first
// searchItineraries request of the user's session. Subsequent itinerary
// requests, and every other search type (hotelSelection, hotelDetails,
// transportList, etc.), must never include it.
export function getRecipeQLForItinerarySearch(
  criteria: ItinerarySearchCriteria
): GraphQL.RecipeQL {
  const recipeCriteria = getRecipeQL(criteria);

  if (isFirstSearch) {
    performanceMark('FirstItineraryRequested');
    recipeCriteria.metadata = {
      ...recipeCriteria.metadata,
      priceAccuracyToken: criteria.priceAccuracyToken,
    };
    isFirstSearch = false;
  }

  return recipeCriteria;
}

export class ItinerarySearchService implements SearchService<
  TripSearchCriteria,
  ItineraryResponse
> {
  public search(criteria: ItinerarySearchCriteria): RecipeResponse {
    if (
      criteria.userCriteria.ingredients === null ||
      criteria.userCriteria?.ingredients.length === 0
    ) {
      return EMPTY;
    }

    const recipeCriteria = getRecipeQLForItinerarySearch(criteria);

    performanceMark(PerformancePoint.ItineraryPricesRequested);

    const itineraryResponse = criteria.isHotelSelectionSearch
      ? getApolloClient().subscribe<
          GraphQL.searchItinerariesHotelSelectionSubscription,
          GraphQL.searchItinerariesHotelSelectionSubscriptionVariables
        >({
          variables: { recipe: recipeCriteria },
          query: SEARCH_ITINERARIES_HOTEL_SELECTION_SUBSCRIPTION,
        })
      : criteria.isPeekPriceSearch
        ? getApolloClient().subscribe<
            GraphQL.searchItinerariesPeekPriceSubscription,
            GraphQL.searchItinerariesPeekPriceSubscriptionVariables
          >({
            variables: { recipe: recipeCriteria },
            query: SEARCH_ITINERARIES_PEEK_PRICE_SUBSCRIPTION,
          })
        : getApolloClient().subscribe<
            GraphQL.searchItinerariesSubscription,
            GraphQL.searchItinerariesSubscriptionVariables
          >({
            variables: { recipe: recipeCriteria },
            query: SEARCH_ITINERARIES_SUBSCRIPTION,
          });

    itineraryResponse
      .pipe(take(1))
      .subscribe(() =>
        performanceMark(PerformancePoint.ItineraryPricesReceived)
      );

    return itineraryResponse.pipe(
      logged('Itinerary Response'),
      map((fetchResult) => fetchResult.data),
      filter(isDefined),
      map((response) =>
        toItineraryResponse(response.searchRecipeByItinerary, criteria)
      )
    );
  }

  public searchSuggestions(
    criteria: ItinerarySearchCriteria
  ): ItinerarySuggestions {
    if (
      criteria.userCriteria.ingredients === null ||
      criteria.userCriteria?.ingredients.length === 0
    ) {
      return {
        cheapestSuggestedItineraries: EMPTY,
        mostPopularSuggestedItineraries: EMPTY,
      };
    }

    const baseRecipeCriteria = getRecipeQL(criteria);
    const mostPopularRecipeCriteria: GraphQL.RecipeQL = {
      ...baseRecipeCriteria,
      suggestedBy: undefined,
      ingredients: baseRecipeCriteria.ingredients?.map((ingredient) => ({
        ...ingredient,
        suggestedBy: GraphQL.InputAvailibityOrderQL.Popular,
      })),
    };
    const cheapestRecipeCriteria: GraphQL.RecipeQL = {
      ...baseRecipeCriteria,
      suggestedBy: undefined,
      ingredients: baseRecipeCriteria.ingredients?.map((ingredient) => ({
        ...ingredient,
        suggestedBy: GraphQL.InputAvailibityOrderQL.Cheapest,
      })),
    };

    const mostPopularItineraryResponse = getApolloClient().subscribe<
      GraphQL.searchItinerarySuggestionPricesMostPopularSubscription,
      GraphQL.searchItinerarySuggestionPricesMostPopularSubscriptionVariables
    >({
      variables: {
        recipe: mostPopularRecipeCriteria,
      },
      query: SEARCH_ITINERARY_SUGGESTION_PRICES_MOST_POPULAR_SUBSCRIPTION,
    });
    const cheapestItineraryResponse = getApolloClient().subscribe<
      GraphQL.searchItinerarySuggestionPricesCheapestSubscription,
      GraphQL.searchItinerarySuggestionPricesCheapestSubscriptionVariables
    >({
      variables: {
        recipe: cheapestRecipeCriteria,
      },
      query: SEARCH_ITINERARY_SUGGESTION_PRICES_CHEAPEST_SUBSCRIPTION,
    });

    return {
      cheapestSuggestedItineraries: cheapestItineraryResponse.pipe(
        map((fetchResult) => fetchResult.data),
        filter(isDefined),
        map((response) =>
          toItinerarySuggestionPrices(response.searchRecipeByItinerary)
        )
      ),
      mostPopularSuggestedItineraries: mostPopularItineraryResponse.pipe(
        map((fetchResult) => fetchResult.data),
        filter(isDefined),
        map((response) =>
          toItinerarySuggestionPrices(response.searchRecipeByItinerary)
        )
      ),
    };
  }
}
